import _ from 'lodash';
import DepsService from 'services/dependencies';
import sessionModule from 'auth/logic/session';
import { applyTokens, updateAuthHeaders } from 'auth/actions/session.actions';

const buffer = 1000;
const SKIPED_ERROR_CODES = [401, 403];
const SYSTEM_UNAVAILABLE = 'SYSTEM_UNAVAILABLE';
const LOGOUT_CODE = 'USER_LOGOUT';
const INVALID_TOKENS = ['INVALID_TOKEN', 'INVALID_REFRESH_TOKEN', 'INVALID_CLIENT_ID', 'TOKEN_LIMIT_EXCEEDED', 'RETAILER_NOT_ALLOWED', 'TOKEN_NOT_FOUND', 'TOKEN_NOT_AUTHORIZED'];

const mapStateToThis = (state) => ({
  session: state.session,
});

const mapDispatchToThis = {
  updateAuthHeaders,
  applyTokens,
};

@Inject('$ngRedux', '$q', '$http', '$sessionStorage', '$state', '$uibModalStack')
export default class HttpService {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
    this.defaults = this.$http.defaults;
    this.requestCanceler = () => this.$q.defer();
    this.pendingRequests = [];
    this.requestsQueue = [];

    this.errorFn = () => {};

    this.rejectPromise = (err) => Promise.reject(err);
    this.reauthQueue = Promise.resolve();
    this.reauthOngoing = false;
  }

  $onDestroy() {
    this.unsubscribe();
  }

  addPendingRequest(canceller, url, key = '') {
    const id = `${new Date().valueOf()}.${Math.random()
      .toString(16)
      .slice(2)}`;
    this.pendingRequests.push({
      id,
      canceller,
      url,
      key,
    });
    return id;
  }

  removePendingRequestById(id) {
    this.pendingRequests = this.pendingRequests.filter((r) => r.id !== id);
  }

  removePendingRequestByKey(key) {
    this.pendingRequests = this.pendingRequests.filter((r) => r.key !== key);
  }

  overwrite(propertyKey, value) {
    this[propertyKey] = value;
  }

  setForceLogoutFn(forceLogoutFn) {
    this.forceLogoutFn = forceLogoutFn;
    return this;
  }

  setReauthFn(reauthFn) {
    this.reauthFn = reauthFn;
    return this;
  }

  validateTokens() {
    const currentTimestamp = Date.now();
    const refreshToken = _.get(this.session, 'tokens.refresh');

    if (!refreshToken) {
      return Promise.reject({ noRefreshToken: true });
    }

    const refreshTokenExpired = refreshToken.expiringTimestamp <= currentTimestamp + buffer;
    if (refreshTokenExpired) {
      return Promise.reject({ refreshTokenExpired: true });
    }
    return this.requestTokens();
  }

  requestTokens(deps, model) {
    const refreshTokenUrl = DepsService.rest.oauth.tokens.url;
    const refreshTokenCode = _.get(this.session, 'tokens.refresh.code') || null;
    const oAuthClientId = _.get(this.session, 'oAuthClientId') || null;

    return DepsService.restSrv.HttpService.putReauth(refreshTokenUrl, {
      token: refreshTokenCode,
      clientId: oAuthClientId,
    })
      .then((response) => this.applyTokens(response.data))
      .catch((err) => Promise.reject({ refreshTokenFailed: true }));
  }

  handleRefreshTokenExpired(response) {
    return this.forceLogoutFn(response);
  }

  handleRefreshTokenFailed(response) {
    return this.forceLogoutFn(response);
  }

  forceLogoutFn(response) {
    const logoutUrl = DepsService.rest.oauth.logout.url;
    let logoutQueue;

    this.pendingRequests.forEach((req) => {
      req.canceller.resolve();
    });
    this.pendingRequests = [];

    if (response.config.url === logoutUrl) {
      logoutQueue = DepsService.$state.go('home-view', null, {
        reload: true,
        skipDirtyForms: true,
      });
    } else {
      logoutQueue = DepsService.ErrorNotificationService.notifyForcedLogout();
    }

    this.$uibModalStack.dismissAll();

    return sessionModule
      .resetSession(DepsService, DepsService.model)
      .then(() => this.resetReauthQueue())
      .then(() => logoutQueue)
      .then(() => Promise.resolve({ code: LOGOUT_CODE }));
  }

  reauthFn(response) {
    const oAuthTokenCode = _.get(this.session, 'tokens.jwt.code');
    const authorizationHeader = `Bearer ${oAuthTokenCode}`;
    if (response.config.headers.Authorization !== authorizationHeader) {
      return this.handleRefreshTokenFailed(response).then(Promise.reject);
    }

    this.reauthQueue = this.validateTokens();
    return this.reauthQueue.then((resp) => resp).catch((err) => {
      let retValue;
      if (err.refreshTokenExpired) {
        retValue = this.handleRefreshTokenExpired(response);
      } else if (err.refreshTokenFailed) {
        retValue = this.handleRefreshTokenFailed(response);
      } else {
        retValue = this.handleRefreshTokenFailed(response);
      }
      return retValue;
    });
  }

  resetReauthQueue() {
    this.reauthQueue = this.$q.resolve();
    return this;
  }

  runReauth(err) {
    this.reauthOngoing = true;
    this.reauthQueue = this.reauthFn(err)
      .then((res) => {
        this.reauthOngoing = false;
        return Promise.resolve(res);
      })
      .catch(() => {
        this.reauthOngoing = false;
        return this.$q.reject(err);
      });

    return this.reauthQueue;
  }

  get(context, url, options, path) {
    const canceller = this.$q.defer();

    const requestFn = () =>
      this.$http.get(
        url,
        _.merge(options, {
          timeout: canceller.promise,
        })
      );
    const pendingRequestId = this.addPendingRequest(canceller, url, path);

    return this.reauthQueue
      .then(requestFn)
      .then((result) => {
        this.removePendingRequestById(pendingRequestId);
        return Promise.resolve(result);
      })
      .catch((err) => this.handleError(err, context, requestFn, url, options, path));
  }

  getFile(context, url, options, method = 'GET', payload = {}, headers = {}) {
    const canceller = this.$q.defer();

    const requestFn = () => this[`requestFunction${method}`](url, options, payload);
    Object.assign(
      options,
      {
        timeout: canceller.promise,
      },
      headers
    );

    return {
      promise: this.reauthQueue.then(requestFn).catch(async (err) => this.handleError(err, context, requestFn, url, options)),
      cancel: (reason) => canceller.resolve(reason),
    };
  }

  requestFunctionGET(url, options) {
    return this.$http.get(url, options);
  }

  requestFunctionPOST(url, options, payload) {
    return this.$http.post(url, payload, options);
  }

  post(context, url, payload, options) {
    const canceller = this.$q.defer();

    const requestFn = () =>
      this.$http.post(
        url,
        payload || null,
        _.merge(options, {
          timeout: canceller.promise,
        })
      );
    const pendingRequestId = this.addPendingRequest(canceller, url);

    return this.reauthQueue
      .then(requestFn)
      .then((result) => {
        this.removePendingRequestById(pendingRequestId);
        return Promise.resolve(result);
      })
      .catch((err) => this.handleError(err, context, requestFn, url, options));
  }

  put(context, url, payload, options) {
    const canceller = this.$q.defer();

    const requestFn = () =>
      this.$http.put(
        url,
        payload || null,
        _.merge(options, {
          timeout: canceller.promise,
        })
      );
    const pendingRequestId = this.addPendingRequest(canceller, url);

    return this.reauthQueue
      .then(requestFn)
      .then((result) => {
        this.removePendingRequestById(pendingRequestId);
        return Promise.resolve(result);
      })
      .catch((err) => this.handleError(err, context, requestFn, url, options));
  }

  putReauth(url, payload, options) {
    const requestFn = () =>
      this.$http.put(
        url,
        payload || null,
        _.merge(options, {
          timeout: this.requestCanceler.promise,
        })
      );
    return this.reauthQueue.then(requestFn);
  }

  delete(context, url, options) {
    const requestFn = () =>
      this.$http.delete(
        url,
        _.merge(options, {
          timeout: this.requestCanceler.promise,
        })
      );
    return this.reauthQueue.then(requestFn).catch((err) => this.handleError(err, context, requestFn, url, options));
  }

  handleError(err, context, requestFn, url, options, path) {
    if (this.reauthOngoing) {
      return this.reauthQueue.then(() => requestFn());
    }

    const code = _.get(err, 'data.errors[0].code');
    if (!code && _.get(err, 'data.code')) {
      err = _.set(err, 'data.errors[0]', err.data || err.code);
    }

    if (err.status === -1) {
      throw err;
    }

    if (INVALID_TOKENS.indexOf(_.get(err, 'data.code')) > -1 && _.get(err, 'status') !== 401) {
      return this.forceLogoutFn(err);
    }

    if (err.status === 401) {
      return this.handle401Error(err, context, requestFn, url, options);
    }

    if (err.status === 409) {
      return this.handleErrorObject(err, context);
    }

    if (err.status && path) {
      return this.handleCriticalError(err, path);
    }

    const typeofReturnDataString = Object.prototype.toString.call(err.data);
    const typeofReturnDataSubstring = typeofReturnDataString.substring(1, typeofReturnDataString.length - 1);
    const typeofReturnData = typeofReturnDataSubstring.split(' ')[1];
    return (_.get(this, `handleError${typeofReturnData}`) || (() => {})).call(this, err, context);
  }

  handle401Error(...args) {
    const [err, context, requestFn] = args;

    return this.handleReauth(err)
      .then(requestFn)
      .catch((error) => {
        if (SKIPED_ERROR_CODES.indexOf(_.get(error, 'status')) > -1 && INVALID_TOKENS.indexOf(_.get(error, 'data.code')) > -1) {
          return this.forceLogoutFn(err);
        }
        if (error.data) {
          this.handleErrorObject(error, context);
        }
        return Promise.reject(error);
      });
  }

  handleErrorObject(err, context) {
    return this.errorFn(err, context);
  }

  handleErrorBlob(err, context) {
    const FileReaderDeffered = this.$q.defer();
    const reader = new FileReader();
    reader.addEventListener('loadend', () => {
      FileReaderDeffered.resolve(this.handleErrorObject(_.set(err, 'data.code', this.getCodeFromArrayBuffer(reader.result)), context));
    });
    reader.readAsArrayBuffer(err.data);
    return FileReaderDeffered.promise;
  }

  handleErrorArrayBuffer(err, context) {
    return this.handleErrorObject(_.set(err, 'data.code', this.getCodeFromArrayBuffer(err.data)), context);
  }

  handleReauth(err) {
    return this.reauthOngoing ? this.reauthQueue : this.runReauth(err);
  }

  getCodeFromArrayBuffer(arrBufResult) {
    return JSON.parse(String.fromCharCode.apply(null, new Uint8Array(arrBufResult))).code;
  }

  handleCriticalError(...args) {
    const [err, path] = args;
    const config = _.get(DepsService, 'config');
    const role = _.get(this, '$sessionStorage.userSession.role') || 'anonymous';
    const criticalError = _.get(config, `roles.${role}.rest.${path}.criticalErrors.${err.status}`);
    return criticalError ? this[`handle_${criticalError}`]() : this.handle_default();
  }

  handle_1() {
    this.$state.go('system-unavailable').then(() => this.handle_default());
  }

  handle_2() {
    return Promise.reject(SYSTEM_UNAVAILABLE);
  }

  handle_3() {
    return Promise.resolve();
  }

  handle_default() {
    throw new Error(SYSTEM_UNAVAILABLE);
  }
}



// WEBPACK FOOTER //
// ./src/modules/utils/rest/http-service.js